import { styled } from "@mui/material/styles";
import { cashierThemePresets } from "../../../constants/cashierThemePresets";

const colors = cashierThemePresets.find((preset) => preset.id === "lime").preview;

/** Scales the cashier layout to a compact preview while retaining its responsive geometry. */
const LimeCashierPreviewRoot = styled("div")({
  width: "100%",
  maxWidth: 1000,
  zoom: 0.7,
  minWidth: 0,
  containerType: "inline-size",
  color: colors.textColor,
  backgroundColor: colors.canvasBackground,
  backgroundImage: "radial-gradient(rgba(11, 11, 12, 0.07) 1px, transparent 1px)",
  backgroundSize: "20px 20px",
  fontFamily: '"Archivo", system-ui, sans-serif',
  fontSize: 14,
  lineHeight: 1.5,
  borderRadius: 12,
  overflow: "hidden",
  "& *, & *::before, & *::after": { boxSizing: "border-box" },
  "& p, & h2": { margin: 0 },
  "& svg": { width: 18, height: 18, flexShrink: 0 },
  "& small": { display: "block", color: colors.subTextColor, fontSize: 11 },
  "& .lp-language": {
    height: 42,
    display: "flex",
    alignItems: "center",
    background: "#ECECE7",
    fontSize: 12,
  },
  "& .lp-language > div": {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 6,
    width: "calc(100% - 32px)",
    maxWidth: 1000,
    margin: "0 auto",
  },
  "& .lp-language svg": { width: 14, height: 14, color: "#888" },
  "& .lp-checkout": {
    containerType: "inline-size",
    width: "calc(100% - 32px)",
    maxWidth: 1000,
    margin: "26px auto 0",
  },
  "& .lp-topbar": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    minHeight: 30,
    marginBottom: 26,
  },
  "& .lp-wordmark": {
    display: "inline-flex",
    fontSize: 25,
    fontWeight: 800,
    letterSpacing: "-0.055em",
    lineHeight: 1.2,
  },
  "& .lp-wordmark span": { position: "relative", isolation: "isolate" },
  "& .lp-wordmark svg": {
    position: "absolute",
    left: -2,
    bottom: -2,
    width: "calc(100% + 5px)",
    height: 8,
    zIndex: -1,
    color: colors.buttonBackground,
  },
  "& .lp-secure": {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    marginLeft: "auto",
    color: colors.subTextColor,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  "& .lp-secure svg": { width: 15, height: 15 },
  "& .lp-columns": {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    border: `1px solid ${colors.cardBorderColor}`,
    borderRadius: 22,
    overflow: "hidden",
    background: "white",
    boxShadow: colors.panelShadow,
  },
  "& .lp-summary, & .lp-summary-bottom": { display: "contents" },
  "& .lp-merchant-header": {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    padding: "24px 28px",
    background: colors.headerBackground,
    borderBottom: `1px solid ${colors.dividerColor}`,
  },
  "& .lp-identity": {
    display: "flex",
    alignItems: "center",
    gap: 11,
    minWidth: 0,
    maxWidth: "100%",
  },
  "& .lp-identity strong": { fontSize: 14, fontWeight: 700, overflowWrap: "anywhere" },
  "& .lp-merchant-mark": {
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    width: 42,
    height: 42,
    border: `1px solid ${colors.dividerColor}`,
    borderRadius: "50%",
    background: "white",
    overflow: "hidden",
  },
  "& .lp-merchant-mark img": { width: "100%", height: "100%", objectFit: "contain" },
  "& .lp-amount > small": { marginBottom: 7 },
  "& .lp-amount > div": { display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 8 },
  "& .lp-amount span": {
    fontFamily: '"Anton", Impact, sans-serif',
    fontSize: 32,
    lineHeight: 1.15,
  },
  "& .lp-amount div small": {
    fontFamily: '"DM Mono", monospace',
    color: colors.textColor,
    fontSize: 10,
  },
  "& .lp-art": { display: "none" },
  "& .lp-protection": {
    order: 2,
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "24px 28px",
    background: colors.headerBackground,
  },
  "& .lp-protection > svg": { width: 25, height: 25 },
  "& .lp-protection strong": { fontSize: 12, fontWeight: 700 },
  "& .lp-protection p": { fontSize: 11, color: colors.subTextColor, marginTop: 3 },
  "& .lp-panel": {
    order: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    padding: "24px 22px 23px",
  },
  "& .lp-progress": {
    display: "flex",
    width: "100%",
    margin: "0 0 28px",
    padding: 0,
    listStyle: "none",
  },
  "& .lp-progress li": {
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 11,
    color: colors.subTextColor,
    whiteSpace: "nowrap",
  },
  "& .lp-progress li:not(:last-child)": { flex: 1 },
  "& .lp-progress li:not(:last-child)::after": {
    content: '""',
    flex: 1,
    minWidth: 12,
    height: 1,
    margin: "0 13px 0 6px",
    background: colors.dividerColor,
  },
  "& .lp-progress li > span": {
    display: "grid",
    placeItems: "center",
    width: 22,
    height: 22,
    flexShrink: 0,
    borderRadius: "50%",
    border: `1px solid ${colors.dividerColor}`,
    fontFamily: '"DM Mono", monospace',
    fontSize: 10,
    fontWeight: 400,
  },
  '& .lp-progress [aria-current="step"]': { color: colors.textColor, fontWeight: 700 },
  '& .lp-progress [aria-current="step"] > span': {
    background: colors.buttonBackground,
    borderColor: colors.textColor,
  },
  "& .lp-heading": { marginBottom: 24 },
  "& .lp-heading h2": {
    fontFamily: '"Anton", Impact, sans-serif',
    fontSize: 36,
    fontWeight: 400,
    lineHeight: 1.14,
    letterSpacing: "0.015em",
    textTransform: "uppercase",
    overflowWrap: "anywhere",
  },
  "& .lp-heading h2 span": { position: "relative", isolation: "isolate", whiteSpace: "nowrap" },
  "& .lp-heading h2 span::before": {
    content: '""',
    position: "absolute",
    zIndex: -1,
    left: "-0.035em",
    right: "-0.035em",
    bottom: "0.06em",
    height: "0.39em",
    background: colors.buttonBackground,
    transform: "rotate(-1.1deg)",
  },
  "& .lp-heading p": { marginTop: 9, color: colors.subTextColor, fontSize: 13, lineHeight: 1.6 },
  "& .lp-methods": { display: "grid", gap: 9 },
  "& .lp-method": {
    appearance: "none",
    display: "flex",
    alignItems: "center",
    gap: 13,
    width: "100%",
    minWidth: 0,
    minHeight: 65,
    padding: "12px 15px",
    background: "white",
    color: colors.textColor,
    border: `1px solid ${colors.optionBorderColor}`,
    borderRadius: 10,
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
  '& .lp-method[aria-pressed="true"]': {
    background: "#F6FAE9",
    borderColor: colors.textColor,
    boxShadow: "inset 0 0 0 0.3px currentColor",
  },
  "& .lp-method:focus-visible, & .lp-continue:focus-visible": {
    outline: "3px solid #3F5406",
    outlineOffset: 3,
  },
  "& .lp-method-icon": {
    display: "grid",
    placeItems: "center",
    width: 36,
    height: 36,
    flexShrink: 0,
    border: `1px solid ${colors.dividerColor}`,
    borderRadius: 8,
    background: "white",
  },
  "& .lp-method-icon svg": { width: 20, height: 20 },
  "& .lp-method-copy": { flex: 1, minWidth: 0, overflowWrap: "anywhere" },
  "& .lp-method-copy strong": { display: "block", fontSize: 13, fontWeight: 700 },
  "& .lp-method-copy small": { marginTop: 2, lineHeight: 1.5 },
  "& .lp-radio": {
    display: "grid",
    placeItems: "center",
    width: 18,
    height: 18,
    flexShrink: 0,
    border: "1px solid #A9A99F",
    borderRadius: "50%",
  },
  '& [aria-pressed="true"] .lp-radio': {
    borderColor: colors.textColor,
    background: colors.buttonBackground,
  },
  '& [aria-pressed="true"] .lp-radio::after': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: colors.textColor,
  },
  "& .lp-actions": { marginTop: "auto", paddingTop: 22 },
  "& .lp-continue": {
    position: "relative",
    width: "100%",
    height: 48,
    borderRadius: 999,
    border: `1px solid ${colors.textColor}`,
    background: colors.buttonBackground,
    color: colors.textColor,
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 700,
  },
  "& .lp-continue svg": { position: "absolute", right: 22, top: 14 },
  "& .lp-actions p": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    marginTop: 12,
    color: colors.subTextColor,
    fontSize: 10,
  },
  "& .lp-actions p svg": { width: 12, height: 12 },
  "& .lp-footer": {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "13px 1px 20px",
    color: colors.subTextColor,
    fontSize: 11,
  },
  "& .lp-footer strong": { color: colors.textColor, marginLeft: 3 },
  "& .lp-cancel": { display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto" },
  "& .lp-cancel svg": { width: 14, height: 14 },
  "@container (min-width: 701px)": {
    "& .lp-columns": {
      display: "grid",
      gridTemplateColumns: "275px minmax(0, 1fr)",
      minHeight: 520,
    },
    "& .lp-summary": {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      background: colors.headerBackground,
      borderRight: `1px solid ${colors.dividerColor}`,
    },
    "& .lp-merchant-header": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 30,
      padding: "30px 28px",
      border: 0,
    },
    "& .lp-amount span": { fontSize: 54 },
    "& .lp-summary-bottom": { display: "flex", flexDirection: "column", marginTop: "auto" },
    "& .lp-art": {
      display: "block",
      width: "100%",
      height: 143,
      objectFit: "cover",
      borderTop: `1px solid ${colors.textColor}`,
      borderBottom: `1px solid ${colors.textColor}`,
    },
    "& .lp-panel": { padding: "29px 38px 25px" },
  },
  "@container (min-width: 901px)": {
    "& .lp-columns": { gridTemplateColumns: "320px minmax(0, 1fr)" },
  },
  "@container (max-width: 600px)": {
    "& .lp-progress": { marginBottom: 20 },
    "& .lp-progress li": { gap: 5, fontSize: 10 },
    "& .lp-progress li:not(:last-child)::after": { margin: "0 8px 0 3px" },
    "& .lp-heading h2": { fontSize: 30 },
  },
  "@container (max-width: 700px)": {
    "& .lp-checkout, & .lp-language > div": { maxWidth: 520 },
  },
  "@container (max-width: 400px)": {
    "& .lp-progress li": { position: "relative", flex: 1, flexDirection: "column" },
    "& .lp-progress li:not(:last-child)::after": {
      position: "absolute",
      top: 11,
      left: "calc(50% + 18px)",
      width: "calc(100% - 36px)",
      minWidth: 0,
      margin: 0,
    },
  },
});

export default LimeCashierPreviewRoot;
